import { useState, useEffect } from 'react';
import clsx from 'clsx';
import useSWR from 'swr';
import useSWRMutation from 'swr/mutation'
import { getVoteStatus, getPtp, postPtp } from 'src/api';
import { useUser } from 'src/context';

const DOOR_RESULT_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/door-result.png"
const DOOR_IN_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/door-in.jpg"
const DOOR_CHARACTER_SOURCE = "https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/door-character.png"

const DoorModal = ({ isOpen, onClose }) => {
  const [inputValue, setInputValue] = useState('');
  const [isCorrect, setIsCorrect] = useState(false);
  const [ranking, setRanking] = useState(0);
  const [tryCount, setTryCount] = useState(0);
  const [countdown, setCountdown] = useState(0);
  const [showError, setShowError] = useState(false);

  const { isLogin } = useUser();

  const { data: voteStatus } = useSWR(`goty2024-voteStatus`, getVoteStatus);

  const { data: ptpData } = useSWR(isLogin ? `goty2024-ptp` : null, getPtp);

  // 檢查 ptpData 是否已包含 ranking，如果有則直接設置為已通關
  useEffect(() => {
    if (ptpData?.data?.ranking) {
      setIsCorrect(true);
      setRanking(ptpData.data.ranking);
      setTryCount(ptpData.data.tryCount || 0);
      setShowError(false);
      setCountdown(0);
    }
  }, [ptpData]);

  const { trigger, isMutating } = useSWRMutation(
    isLogin ? `goty2024-ptp` : null, 
    (url, { arg }) => postPtp(arg),
    {
      onSuccess: (res) => {
        if (res.data.isCorrect) {
          // 顯示 res.data.ranking 顯示目前為第幾名以及答題嘗試次數
          setIsCorrect(true);
          setRanking(res.data.ranking);
          setTryCount(res.data.tryCount);
          setShowError(false);
          setCountdown(0);
        } else {
          // isCorrect: false，則顯示密語錯誤，並倒數 60 秒後才能再次送出
          setIsCorrect(false);
          setShowError(true);
          setCountdown(60);
        }
      },
    }
  );

  // 倒數計時器效果
  useEffect(() => {
    let timer;
    if (countdown > 0) {
      timer = setTimeout(() => {
        setCountdown(countdown - 1);
      }, 1000);
    } else if (countdown === 0 && showError) {
      setShowError(false);
    }
    return () => {
      if (timer) clearTimeout(timer);
    };
  }, [countdown, showError]);

  const ptpStep = ptpData?.data?.step || 0;
  const isOpening = voteStatus?.data?.event === "OPEN_SECOND";
  const isSubmitEnabled = isOpening && ptpStep === 4 && countdown === 0;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!inputValue.trim() || !isSubmitEnabled) return;

    trigger({
      answer: inputValue.trim()
    });
  };

  useEffect(() => {
    if (isOpen) {
      // 阻止 body 滾動
      document.body.style.overflow = 'hidden';
    } else {
      // 恢復 body 滾動
      document.body.style.overflow = 'unset';
    }

    // 清理函數：組件卸載時恢復滾動
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) {
    return null;
  }

  return (
    <div className={clsx("fixed inset-0 z-[2000] flex items-center justify-center transition-all p-4 bg-[#317E70]", {
      'bg-[url("https://storage.googleapis.com/escape-bar-firebase.appspot.com/static/goty2024/hero-bg.png")] bg-cover bg-center bg-no-repeat': isCorrect
    })}>
      <button
        className="absolute top-4 right-4 text-white text-sm font-bold border-2 border-white rounded-lg px-3 py-1.5 z-60"
        onClick={(e) => { e.stopPropagation(); onClose(); }}
        aria-label="關閉"
      >
        離開
      </button>
      <div
        className="flex flex-col items-center justify-center relative w-full max-w-sm md:max-w-lg gap-8"
        onClick={e => e.stopPropagation()}
      >
        <div className="w-full flex justify-center relative">
          <img
            src={isCorrect ? DOOR_RESULT_SOURCE : DOOR_IN_SOURCE}
            className="object-contain w-full"
          />
          {isCorrect && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-black text-xl md:text-3xl font-bold">
                {ranking}
              </span>
            </div>
          )}
          <img src={DOOR_CHARACTER_SOURCE} className={clsx('absolute w-24', {
            'bottom-1/2 right-1/2 translate-x-1/2 translate-y-1/2': !isCorrect,
            'bottom-0 right-0 w-24 md:w-32': isCorrect
          })} />
        </div>

        <div className="w-full flex flex-col justify-center">
          {isCorrect && (
            <>
              <h3 className="text-xl md:text-2xl font-bold text-center mb-4 text-white">
                恭喜通關！
              </h3>
              <p className="text-center text-white mb-4">
                你在第四屆金逃獎【金逃影展】期間活躍於夜間時段，破解金逃大戲院箇中奧妙，成功進入斐逃斐榮譽學會第一會所。斐逃斐榮譽學會認可你的表現，頒發「零時演員」成就給你。
              </p>
              <p className="text-center text-white">
                回答錯誤次數：{tryCount} 
              </p>
            </>
          )}
          {!isCorrect && (
            <div className="relative bg-white p-3 md:p-4 rounded-lg shadow-md">
              <div className="text-black text-sm md:text-base">
                <h3 className="text-lg md:text-xl font-bold text-center mb-4 text-gray-800">
                  請輸入通關密語
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div className="space-y-2 md:space-y-3">
                    <input
                      type="text"
                      value={inputValue}
                      onChange={(e) => setInputValue(e.target.value)}
                      placeholder="請輸入..."
                      maxLength={20}
                      className="w-full px-4 py-3 border-2 border-[#7B2337] rounded-lg focus:border-[#7B2337] focus:outline-none text-gray-800 placeholder-gray-500"
                      disabled={isMutating || countdown > 0}
                    />
                    {showError && countdown > 0 && (
                      <p className="text-red-600 text-center text-sm">
                        密語錯誤，請等待 {countdown} 秒後再送出（答題無次數限制）
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={!inputValue.trim() || isMutating || countdown > 0}
                      className={clsx(`relative w-full p-2 text-center border-2 rounded-lg transition-all duration-300 group overflow-hidden`, {
                        'bg-gray-300 border-gray-300 text-gray-500 cursor-not-allowed': !inputValue.trim() || isMutating || countdown > 0,
                        'bg-white border-[#7B2337] text-[#7B2337] hover:bg-[#7B2337] hover:text-white': inputValue.trim() && !isMutating && countdown === 0
                      })}
                    >
                      <div className="flex items-center justify-center">
                        <div className="text-center">{isMutating ? '送出中...' : '送出'}</div>
                      </div>
                    </button>
                  </div>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default DoorModal; 